import React from 'react';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';

const WIDTH = 1080;
const HEIGHT = 1080;

function GraphicPreviewStack({
  graphicData,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  dominantImageScale = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageOpacity = 100,
  detailTextColor = null,
  dateFormat = 'full',
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;
  const primary = teamColors.primary;
  const secondary = teamColors.secondary;
  const hasImage = Boolean(dominantImage);
  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const accentColor = accentBlockColor || secondary;
  const scale = (dominantImageScale ?? 100) / 100;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlow = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${primary} 0%, ${secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${primary} 0%, ${secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${primary} 50%, ${secondary} 50%)`;
      case 'solid-primary':
        return primary;
      case 'solid-secondary':
        return secondary;
      default:
        return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
    }
  };

  return (
    <div
      id="graphic-preview"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1 — Background photo */}
      {backgroundPhoto && (
        <img
          src={backgroundPhoto}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: `${bgImageY}%`,
            left: `${bgImageX}%`,
            transform: `translate(-50%, -50%) scale(${bgImageScale})`,
            transformOrigin: 'center center',
            width: 'auto',
            height: 'auto',
            minWidth: '100%',
            minHeight: '100%',
            maxWidth: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Layer 2 — Dark scrim for readability */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 2,
        }}
      />

      {/* Layer 3 — Dominant image, right side, full height bleed */}
      {hasImage && (
        <img
          src={dominantImage}
          alt=""
          style={{
            position: 'absolute',
            top: `${dominantImageY ?? 50}%`,
            left: `${dominantImageX ?? 50}%`,
            transform: `translate(-50%, -50%) scale(${scale})`,
            opacity: (dominantImageOpacity ?? 100) / 100,
            height: HEIGHT * scale,
            width: 'auto',
            maxWidth: 'none',
            zIndex: 3,
          }}
        />
      )}

      {/* Layer 4 — Left vertical color bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 16,
          height: HEIGHT,
          background: primary,
          zIndex: 4,
        }}
      />

      {/* Layer 5a — "GAME" hero text */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 80,
          fontFamily: heroFontFamily,
          fontSize: 220,
          lineHeight: 1,
          color: '#ffffff',
          letterSpacing: 6,
          zIndex: 5,
        }}
      >
        GAME
      </div>

      {/* Layer 5b — "DAY" — accent color, slight vertical overlap with GAME */}
      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 80,
          fontFamily: heroFontFamily,
          fontSize: 220,
          lineHeight: 1,
          color: accentColor,
          letterSpacing: 6,
          zIndex: 5,
        }}
      >
        DAY
      </div>

      {/* Layer 5c — Horizontal rule */}
      <div
        style={{
          position: 'absolute',
          top: 460,
          left: 80,
          width: 200,
          height: 6,
          background: accentColor,
          zIndex: 5,
        }}
      />

      {/* Layer 5d — Home team name */}
      <div
        style={{
          position: 'absolute',
          top: 490,
          left: 80,
          right: hasImage ? 560 : 80,
          fontFamily: heroFontFamily,
          fontSize: 72,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          letterSpacing: 3,
          textShadow: '0 2px 24px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          zIndex: 5,
        }}
      >
        {homeTeam || 'YOUR TEAM'}
      </div>

      {/* Layer 5e — VS */}
      <div
        style={{
          position: 'absolute',
          top: 580,
          left: 80,
          fontFamily: barlow,
          fontSize: 48,
          fontWeight: 700,
          lineHeight: 1,
          color: detailTextColor || accentColor,
          letterSpacing: 8,
          zIndex: 5,
        }}
      >
        VS
      </div>

      {/* Layer 5f — Away team name */}
      <div
        style={{
          position: 'absolute',
          top: 640,
          left: 80,
          right: hasImage ? 560 : 80,
          fontFamily: heroFontFamily,
          fontSize: 60,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          letterSpacing: 3,
          textShadow: '0 2px 24px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          zIndex: 5,
        }}
      >
        {awayTeam || 'OPPONENT'}
      </div>

      {/* Layer 5g — Date / time / location */}
      <div
        style={{
          position: 'absolute',
          top: 780,
          left: 80,
          right: hasImage ? 560 : 80,
          fontFamily: barlow,
          fontSize: 36,
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: 1,
          color: detailTextColor || 'rgba(255,255,255,0.75)',
          zIndex: 5,
        }}
      >
        {formatGameDate(gameDate, dateFormat) && <div>{formatGameDate(gameDate, dateFormat)}</div>}
        {gameTime && <div>{formatGameTime(gameTime)}</div>}
        {location && <div>{location}</div>}
      </div>

      {/* Layer 6 — Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 6,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicPreviewStack;
